import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import { useCurrentSite } from "../../../contexts/models/SiteContext";
import { Comment } from "../../../resources/modelTypes";
import { RootState } from "../../../resources/reduxTypes";
import { getResource } from "../../selectors";
import CommentCardSection from "./CommentCardSection";
import { NewCommentForm } from "./NewCommentForm";
import { css } from "@emotion/react";
import { Button as FancyButton, TinyHeadline } from "@minted/fancyclothes";
import { theme, GlobalStyles, styleUtils } from "@minted/minted-components";
import React, { useMemo, useState, useCallback, memo } from "react";
import { connect } from "react-redux";

const centeredContainerStyles = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const commentButtonContainerStyles = (hasComments: boolean) => css`
  margin-bottom: ${hasComments ? theme.spacing.x6 : 0};
  max-width: ${styleUtils.rem(600)};
  width: 100%;
`;

const commentButtonContainerStylesOnlineInvitation = (
  hasComments: boolean,
) => css`
  margin-bottom: ${hasComments ? theme.spacing.x6 : 0};
  width: 100%;
  max-width: ${styleUtils.rem(500)};
`;

const commentSectionStyles = css`
  padding-left: ${theme.spacing.x6};
  padding-right: ${theme.spacing.x6};
`;

const countCommentsStyles = css`
  margin-bottom: ${theme.spacing.x2};
  overflow-wrap: break-word;
  text-align: center;
`;

export const countComments = (comments: Array<Comment>) =>
  comments.reduce(
    (sum, comment) => sum + 1 + (comment.replies?.length || 0),
    0,
  );

type CommentSectionPropsWithRedux = {
  comments: Array<Comment>;
};

const CommentSection = ({ comments }: CommentSectionPropsWithRedux) => {
  const site = useCurrentSite();
  const { isOnlineInvitation } = site;
  const { isDemo, isPreview } = useIsPreviewOrDemo();
  const isReadOnly = isPreview || isDemo;

  const [isWritingComment, setIsWritingComment] = useState(false);
  const [replyId, setReplyId] = useState(undefined);

  const closeDrawer = useCallback(() => {
    setIsWritingComment(false);
  }, []);

  const startParentComment = useCallback(() => {
    setIsWritingComment(true);
    setReplyId(undefined);
  }, []);
  const startReplyComment = useCallback(
    (replyId) => () => {
      setIsWritingComment(true);
      setReplyId(replyId);
    },
    [],
  );

  const countOfComments = useMemo(() => countComments(comments), [comments]);
  const hasComments = countOfComments > 0;

  const commentText = useMemo(() => {
    if (countOfComments > 0) {
      return countOfComments > 1 ? `${countOfComments} comments` : "1 comment";
    }

    return "Be the first one to write a comment";
  }, [countOfComments]);

  return (
    <div css={commentSectionStyles} id="comment_section">
      <GlobalStyles />
      <div css={centeredContainerStyles}>
        <div css={countCommentsStyles}>
          <TinyHeadline>{commentText}</TinyHeadline>
        </div>
        <div
          css={
            isOnlineInvitation
              ? commentButtonContainerStylesOnlineInvitation(hasComments)
              : commentButtonContainerStyles(hasComments)
          }
        >
          <FancyButton
            block={true}
            disabled={isReadOnly}
            onClick={startParentComment}
            text="Write Comment"
          />
        </div>
        <NewCommentForm
          closeDrawer={closeDrawer}
          isWritingComment={isWritingComment}
          replyId={replyId}
        />
        <CommentCardSection
          comments={comments}
          isDeleteEnabled={site.isTenant}
          isReadOnly={isReadOnly}
          startReplyComment={startReplyComment}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  comments: getResource(state.resources.comments),
});

const ConnectedCommentSection = connect(mapStateToProps)(
  CommentSection,
) as unknown as React.FC;

const MemoizedCommentSection = memo(ConnectedCommentSection);

export { MemoizedCommentSection as CommentSection };
